import { Box, Card, styled, Button, TextField, MenuItem, Typography, CircularProgress, alpha, Dialog, DialogTitle, DialogContent, DialogActions, Alert, IconButton } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { useEffect, useState, useCallback, useRef } from "react";
import EnquiryFormTable from "page-sections/EnquiryForm/EnquiryFormTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { GetApp, Search, CloudUpload, Close } from "@mui/icons-material";
import EnquiryFormExportModal from "page-sections/EnquiryForm/EnquiryFormExportModal";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import buttonConfig from "config/ConfigAccessButton";

const EnquiryForm = () => {
    const { setPageTitle } = usePageTitle();
    const toast = useToast();
    const [enquiryForms, setEnquiryForms] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [companyFilter, setCompanyFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [totalEnquiries, setTotalEnquiries] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    
    // Enquiry Upload states
    const [enquiryUploadDialogOpen, setEnquiryUploadDialogOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const fileInputRef = useRef(null);
    
    const [pagination, setPagination] = useState({
        current_page: 1,
        page_size: 10,
        total_items: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        next_page_number: null,
        previous_page_number: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [10, 25, 50, 100];

    // Check if user is Company type
    const isCompanyUser = apiService.isCompanyUser();
    const companyId = apiService.getCompanyId();

    // ============ PERMISSION FUNCTIONS ============
    const getCurrentButtonPermissions = () => {
        const moduleConfig = buttonConfig["Enquiry form"];
        return moduleConfig ? moduleConfig : {};
    };
    
    const shouldEnableButton = (buttonKey) => {
        const permissions = getCurrentButtonPermissions();
        return permissions[buttonKey] === true;
    };
    // ============ END PERMISSION FUNCTIONS ============

    useEffect(() => {
        setPageTitle('Enquiry Form');
    }, [setPageTitle]);

    // Fetch companies (for admin users only)
    const fetchCompanies = useCallback(async () => {
        if (isCompanyUser) {
            const details = apiService.getCompanyDetails();
            const id = apiService.getCompanyId();
            const singleCompany = {
                id: id,
                name: details?.name || "My Company",
            };
            setCompanyOptions([singleCompany]);
            setCompanyFilter(id?.toString() || "");
            return;
        }

        setLoadingCompanies(true);
        try {
            const response = await apiService.get(API_ENDPOINTS.GET_company_choices);
            const options = response?.data?.Company || response?.data?.choices || response?.data || [];
            if (Array.isArray(options)) {
                setCompanyOptions(options);
            } else {
                setCompanyOptions([]);
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            setCompanyOptions([]);
        } finally {
            setLoadingCompanies(false);
        }
    }, [isCompanyUser]);

    // Fetch categories from API based on selected company
    const fetchCategories = useCallback(async (selectedCompanyId = null) => {
        setLoadingCategories(true);
        try {
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            
            if (!targetCompanyId) {
                setCategories([]);
                return;
            }

            const userId = localStorage.getItem('user_id');
            const response = await apiService.get(`${API_ENDPOINTS.GET_product_category}?company=${targetCompanyId}&user_id=${userId}`);
            
            if (response?.success) {
                const categoryList = response.data?.ProductCategory || [];
                setCategories(categoryList);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            setCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    }, [isCompanyUser, companyId]);

    // Fetch enquiry forms with company_id filter, search, and pagination
    const fetchEnquiryForms = useCallback(async (selectedCompanyId = null, page = 1, pageSizeParam = 10, search = "") => {
        setLoading(true);
        try {
            let url = API_ENDPOINTS.GET_enquiry_form;
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('page_size', String(pageSizeParam));
            
            if (targetCompanyId) {
                params.set('company_id', targetCompanyId);
            }
            
            if (search && search.trim()) {
                params.set('search', search.trim());
            }
            
            url += (url.includes('?') ? '&' : '?') + params.toString();

            const response = await apiService.get(url);

            if (response?.success && response?.data?.EnquiryForm) {
                setEnquiryForms(response.data.EnquiryForm);
            } else {
                setEnquiryForms([]);
            }
            const paginationData = response?.pagination || response?.data?.pagination || {};
            if (Object.keys(paginationData).length > 0) {
                setPagination({
                    current_page: paginationData.current_page ?? page,
                    page_size: paginationData.page_size ?? pageSizeParam,
                    total_items: paginationData.total_items ?? 0,
                    total_pages: paginationData.total_pages ?? 1,
                    has_next: paginationData.has_next ?? false,
                    has_previous: paginationData.has_previous ?? false,
                    next_page_number: paginationData.next_page_number ?? null,
                    previous_page_number: paginationData.previous_page_number ?? null,
                });
                setCurrentPage(paginationData.current_page ?? page);
                setPageSize(paginationData.page_size ?? pageSizeParam);
            } else {
                const list = response?.data?.EnquiryForm || [];
                setPagination(prev => ({
                    ...prev,
                    total_items: list.length,
                    total_pages: 1,
                    current_page: 1,
                    page_size: pageSizeParam,
                }));
            }
        } catch (error) {
            console.error("API error:", error);
            setEnquiryForms([]);
        } finally {
            setLoading(false);
        }
    }, [isCompanyUser, companyId]);

    const handleEnquiryPageChange = (_, value) => {
        setCurrentPage(value);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchEnquiryForms(targetCompanyId, value, pageSize, searchTerm);
    };

    const handleEnquiryPageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setCurrentPage(1);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchEnquiryForms(targetCompanyId, 1, newSize, searchTerm);
    };

    // Handle search
    const handleSearch = () => {
        if (!searchValue.trim()) return;
        setSearchTerm(searchValue);
        setHasSearched(true);
        setCurrentPage(1);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchEnquiryForms(targetCompanyId, 1, pageSize, searchValue);
    };

    // Handle clear search
    const handleClearSearch = () => {
        setSearchValue("");
        setSearchTerm("");
        setCurrentPage(1);
        setHasSearched(false);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchEnquiryForms(targetCompanyId, 1, pageSize, "");
    };

    // Handle Enter key press
    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && searchValue.trim()) {
            handleSearch();
        }
    };

    // Fetch dashboard data for total enquiries
    const fetchDashboardData = useCallback(async () => {
        setLoadingDashboard(true);
        try {
            const response = await apiService.get(API_ENDPOINTS.GET_farmer_enquiry_dashboard);
            if (response?.success && response?.data?.summary) {
                setTotalEnquiries(response.data.summary.total_enquiries);
            } else {
                setTotalEnquiries(null);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setTotalEnquiries(null);
        } finally {
            setLoadingDashboard(false);
        }
    }, []);

    // Fetch companies on component mount
    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    // Fetch categories when company changes
    useEffect(() => {
        if (companyFilter || (isCompanyUser && companyId)) {
            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
            fetchCategories(targetCompanyId);
            setCategoryFilter("");
        } else {
            setCategories([]);
            setCategoryFilter("");
        }
    }, [companyFilter, isCompanyUser, companyId, fetchCategories]);

    // Fetch enquiry forms on component mount and when company changes
    useEffect(() => {
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchEnquiryForms(targetCompanyId, currentPage, pageSize, searchTerm);
    }, [companyFilter, isCompanyUser, companyId, fetchEnquiryForms]);

    // Fetch dashboard data on component mount
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Auto-refresh when search input is cleared
    useEffect(() => {
        if (searchValue === "" && searchTerm !== "") {
            setSearchTerm("");
            setCurrentPage(1);
            setHasSearched(false);
            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
            fetchEnquiryForms(targetCompanyId, 1, pageSize, "");
        }
    }, [searchValue, searchTerm, companyFilter, isCompanyUser, companyId, pageSize, fetchEnquiryForms]);

    // Handle company filter change
    const handleCompanyFilterChange = (e) => {
        setCompanyFilter(e.target.value);
        setSearchTerm("");
        setSearchValue("");
        setHasSearched(false);
    };

    // Handle category filter change
    const handleCategoryFilterChange = (e) => {
        setCategoryFilter(e.target.value);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        if (targetCompanyId) {
            fetchEnquiryForms(targetCompanyId, currentPage, pageSize, searchTerm);
        }
    };

    // Export handlers
    const handleExportClick = () => {
        setExportModalOpen(true);
    };

    const handleExportModalClose = () => {
        setExportModalOpen(false);
    };

    // Enquiry Upload Handlers
    const openEnquiryUploadDialog = () => {
        setEnquiryUploadDialogOpen(true);
        setUploadFile(null);
        setUploadError("");
        setUploadSuccess(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const closeEnquiryUploadDialog = () => {
        setEnquiryUploadDialogOpen(false);
        setUploadFile(null);
        setUploadError("");
        setUploadSuccess(false);
        setUploading(false);
        setDownloadingTemplate(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Get auth token from localStorage
    const getAuthToken = () => {
        const possibleKeys = [
            'access_token',
            'token',
            'auth_token',
            'accessToken',
            'Authorization',
            'bearer_token'
        ];
        
        for (const key of possibleKeys) {
            const token = localStorage.getItem(key);
            if (token && token !== 'undefined' && token !== 'null' && token !== '') {
                return token;
            }
        }
        
        for (const key of possibleKeys) {
            const token = sessionStorage.getItem(key);
            if (token && token !== 'undefined' && token !== 'null' && token !== '') {
                return token;
            }
        }
        
        return null;
    };

    // Download Template Handler
    const handleDownloadTemplate = async () => {
        setDownloadingTemplate(true);
        setUploadError("");
        
        try {
            const token = getAuthToken();
            if (!token) {
                throw new Error("No authentication token found. Please login again.");
            }

            const response = await fetch('https://devdashboard.nexgenagritech.ai/api/m2/enquiry_form/download-template/', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*'
                }
            });

            if (!response.ok) {
                throw new Error(`Download failed with status: ${response.status}`);
            }

            const blob = await response.blob();
            
            if (blob.size === 0) {
                throw new Error("Downloaded file is empty");
            }
            
            if (blob.type === 'text/html' || blob.type.includes('html')) {
                const text = await blob.text();
                console.error("Received HTML error:", text.substring(0, 500));
                throw new Error("Server returned an error page. Please check authentication.");
            }
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'enquiry_template.xlsx';
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            } else {
                const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
                filename = `enquiry_template_${timestamp}.xlsx`;
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.showSuccess("Template downloaded successfully");
            
        } catch (error) {
            console.error("Template download failed:", error);
            const errorMessage = error?.message || "Failed to download template. Please try again.";
            setUploadError(errorMessage);
            toast.showError(errorMessage);
        } finally {
            setDownloadingTemplate(false);
        }
    };

    // File Upload Handlers
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            const validExtensions = ['.xlsx', '.xls', '.xlsm', '.csv'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            
            if (validExtensions.includes(fileExtension)) {
                setUploadFile(file);
                setUploadError("");
                setUploadSuccess(false);
            } else {
                setUploadError("Please select a valid Excel file (.xlsx, .xls, .xlsm, or .csv)");
                event.target.value = null;
            }
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file && (file.type.includes('sheet') || file.name.match(/\.(xlsx|xls|xlsm|csv)$/))) {
            setUploadFile(file);
            setUploadError("");
            setUploadSuccess(false);
        } else {
            setUploadError("Please select a valid Excel file (.xlsx, .xls, .xlsm, or .csv)");
        }
    };

    const handleEnquirykUpload = async () => {
        if (!uploadFile) {
            setUploadError("Please select a file to upload");
            return;
        }

        setUploading(true);
        setUploadError("");
        setUploadSuccess(false);

        try {
            const formData = new FormData();
            formData.append("excel_file", uploadFile);
            
            const userId = localStorage.getItem('user_id');
            if (userId) {
                formData.append("added_by", userId);
            }

            const token = getAuthToken();
            
            const response = await apiService.post(API_ENDPOINTS.POST_enquiry_excel, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response?.success) {
                setUploadSuccess(true);
                toast.showSuccess(response?.message || "Enquiries uploaded successfully");
                setTimeout(() => {
                    closeEnquiryUploadDialog();
                    const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                    fetchEnquiryForms(targetCompanyId, currentPage, pageSize, searchTerm);
                    fetchDashboardData();
                }, 2000);
            } else {
                setUploadError(response?.message || "Upload failed. Please try again.");
                toast.showError(response?.message || "Upload failed");
            }
        } catch (error) {
            console.error("Enquiry Upload failed:", error);
            const errorMessage = error?.response?.data?.message || error?.message || "Upload failed. Please check your file and try again.";
            setUploadError(errorMessage);
            toast.showError(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box pt={2} pb={4}>
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: (theme) => theme.zIndex.appBar - 1,
                    backgroundColor: "background.default",
                    pt: 0.5,
                    pb: 1,
                    mb: 1,
                }}
            >
                <FlexBetween flexWrap="wrap" mb={2}>
                    <FlexBox alignItems="center" gap={2} style={{ flexGrow: 1 }}>
                        <SearchInput
                            bordered={false}
                            placeholder="Search by Date, farmer name, Contact, village etc"
                            onChange={e => {
                                setSearchValue(e.target.value);
                                if (hasSearched && e.target.value !== searchTerm) {
                                    setHasSearched(false);
                                }
                            }}
                            value={searchValue}
                            onKeyPress={handleKeyPress}
                            sx={{
                                width: "51%",
                                maxWidth: "none !important"
                            }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<Search />}
                            onClick={handleSearch}
                            disabled={!searchValue.trim() || (searchValue === searchTerm && hasSearched)}
                            sx={{ textTransform: "none", minWidth: 100 }}
                        >
                            Search
                        </Button>
                    </FlexBox>
                    <Box>
                        <Box display="flex" gap={1}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<CloudUpload />}
                                onClick={openEnquiryUploadDialog}
                                disabled={!shouldEnableButton('enquiry_upload')}
                                sx={{ 
                                    textTransform: 'none',
                                    opacity: !shouldEnableButton('enquiry_upload') ? 0.6 : 1
                                }}
                            >
                                Enquiry Upload
                            </Button>
                            
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                disabled={!shouldEnableButton('export_to_excel')}
                                sx={{ 
                                    textTransform: 'none',
                                    opacity: !shouldEnableButton('export_to_excel') ? 0.6 : 1
                                }}
                            >
                                Export to Excel
                            </Button>
                        </Box>
                    </Box>
                </FlexBetween>

                {/* Filter by Company (admin users only) */}
                {!isCompanyUser && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <Typography variant="body2" fontWeight={600}>
                            Filter by category:
                        </Typography>

                        <TextField
                            select
                            size="small"
                            label="All Category"
                            value={companyFilter}
                            onChange={handleCompanyFilterChange}
                            disabled={loading || loadingCompanies}
                            sx={{ minWidth: { xs: "100%", sm: 220 } }}
                        >
                            <MenuItem value="">All Company</MenuItem>
                            {loadingCompanies ? (
                                <MenuItem disabled>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    Loading companies...
                                </MenuItem>
                            ) : (
                                companyOptions.map((company) => (
                                    <MenuItem key={company.id} value={company.id}>
                                        {company.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>

                        {/* Total Enquiries Display - Next to filter */}
                        {totalEnquiries !== null && (
                            <Box
                                sx={{
                                    minWidth: { xs: "100%", sm: 220 },
                                    height: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    paddingLeft: "10px",
                                    backgroundColor: (theme) =>
                                        theme.palette.mode === "dark"
                                            ? "rgba(255, 255, 255, 0.05)"
                                            : "rgba(0, 0, 0, 0.02)",
                                    borderRadius: 1,
                                    border: (theme) =>
                                        `1px solid ${
                                            theme.palette.mode === "dark"
                                                ? "rgba(255, 255, 255, 0.23)"
                                                : "rgba(0, 0, 0, 0.23)"
                                        }`,
                                }}
                            >
                                <Typography variant="body1" color="text.primary">
                                    Total Enquiries: {totalEnquiries.toLocaleString()}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Total Enquiries Display - For company users (when filter is not shown) */}
                {isCompanyUser && totalEnquiries !== null && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <Typography variant="body2" fontWeight={600}>
                            Filter by category:
                        </Typography>
                        <Box
                            sx={{
                                position: "relative",
                                minWidth: { xs: "100%", sm: 220 },
                                minHeight: "40px",
                                height: "56px",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-end",
                                px: 1.5,
                                pb: 1.25,
                                pt: 2,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                    ? alpha(theme.palette.primary.main, 0.08)
                                    : alpha(theme.palette.primary.main, 0.04),
                                borderRadius: 1,
                                border: (theme) => `1px solid ${theme.palette.mode === 'dark' 
                                    ? alpha(theme.palette.primary.main, 0.3)
                                    : alpha(theme.palette.primary.main, 0.2)}`,
                                boxShadow: (theme) => theme.palette.mode === 'dark'
                                    ? '0 1px 3px rgba(0, 0, 0, 0.2)'
                                    : '0 1px 3px rgba(0, 0, 0, 0.08)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                                    boxShadow: (theme) => theme.palette.mode === 'dark'
                                        ? '0 2px 6px rgba(0, 0, 0, 0.3)'
                                        : '0 2px 6px rgba(0, 0, 0, 0.12)',
                                },
                            }}
                        >
                            <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                    position: "absolute",
                                    top: "9px",
                                    left: "14px",
                                    fontSize: "0.75rem",
                                    lineHeight: 1,
                                    fontWeight: 500,
                                    letterSpacing: "0.02em",
                                }}
                            >
                                Total Enquiries
                            </Typography>
                            {loadingDashboard ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '24px' }}>
                                    <CircularProgress size={16} />
                                </Box>
                            ) : (
                                <Typography 
                                    variant="body2" 
                                    fontWeight={700} 
                                    color="primary.main" 
                                    sx={{ 
                                        lineHeight: 1.4375,
                                        fontSize: "0.9375rem",
                                        letterSpacing: "0.01em",
                                    }}
                                >
                                    {totalEnquiries.toLocaleString()}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            <Card>
                <Box sx={{ mt: 1 }}>
                    <EnquiryFormTable
                        enquiryForms={enquiryForms}
                        searchQuery={searchTerm}
                        loading={loading}
                        onRefresh={() => {
                            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                            fetchEnquiryForms(targetCompanyId, currentPage, pageSize, searchTerm);
                            fetchDashboardData();
                        }}
                        pagination={pagination}
                        onPageChange={handleEnquiryPageChange}
                        onPageSizeChange={handleEnquiryPageSizeChange}
                        pageSizeOptions={pageSizeOptions}
                    />
                </Box>
            </Card>

            <EnquiryFormExportModal
                open={exportModalOpen}
                onClose={handleExportModalClose}
                onRefresh={() => {
                    const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                    fetchEnquiryForms(targetCompanyId, currentPage, pageSize, searchTerm);
                }}
            />

            {/* Enquiry Upload Dialog */}
            <Dialog open={enquiryUploadDialogOpen} onClose={closeEnquiryUploadDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CloudUpload color="primary" />
                            <Typography variant="h6">Upload Enquiries</Typography>
                        </Box>
                        <IconButton onClick={closeEnquiryUploadDialog} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                
                <DialogContent sx={{
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.05)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.2)'
                            : 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                        '&:hover': {
                            background: (theme) => theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.3)'
                                : 'rgba(0, 0, 0, 0.3)',
                        },
                    },
                    scrollbarWidth: 'thin',
                }}>
                    <Box sx={{ mt: 2 }}>
                        {/* Step 1: Format Check */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.1), borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Step 1: Check Your Format
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                If you already have data in the correct format with all required columns, you can skip to Step 3. 
                                Otherwise, download the template to ensure proper formatting.
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                Required columns: Farmer Name, Village, Reference, Product, SKU, etc.
                            </Typography>
                        </Box>

                        {/* Download Template Section */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: (theme) => alpha(theme.palette.info.main, 0.1), borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Step 2: Download Template (Optional)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                Download the Excel template to ensure your data is in the correct format.
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={downloadingTemplate ? <CircularProgress size={16} /> : <GetApp />}
                                onClick={handleDownloadTemplate}
                                disabled={downloadingTemplate}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {downloadingTemplate ? (
                                    <>
                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                        Downloading...
                                    </>
                                ) : (
                                    'Download Template'
                                )}
                            </Button>
                        </Box>

                        {/* Upload Section */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Step 3: Upload Your File
                            </Typography>
                            
                            <Box
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                sx={(theme) => ({
                                    border: `2px dashed ${uploadFile ? theme.palette.primary.main : theme.palette.divider}`,
                                    borderRadius: theme.shape.borderRadius * 2,
                                    padding: theme.spacing(4),
                                    textAlign: 'center',
                                    backgroundColor: uploadFile ? alpha(theme.palette.primary.main, 0.1) : theme.palette.background.paper,
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    mb: 2,
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                    }
                                })}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls,.xlsm,.csv"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                
                                <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                
                                {uploadFile ? (
                                    <Box>
                                        <Typography variant="h6" color="primary">
                                            {uploadFile.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                            Click to select Excel file or drag & drop
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Supports .xlsx, .xls, .xlsm, and .csv files
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Error Message */}
                        {uploadError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {uploadError}
                            </Alert>
                        )}

                        {/* Success Message */}
                        {uploadSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                File uploaded successfully! Enquiries are being processed.
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                
                <DialogActions>
                    <Button 
                        onClick={closeEnquiryUploadDialog} 
                        disabled={uploading}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEnquirykUpload}
                        variant="contained"
                        disabled={!uploadFile || uploading}
                        startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EnquiryForm;